import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

const TypewriterText = ({ text, onComplete, delay = 30 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, delay, onComplete]);

  return <span>{displayedText}</span>;
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [typing, setTyping] = useState(true);
  const [formData, setFormData] = useState({
    revenueGoal: "",
    companyName: "",
    products: "",
    icp: ""
  });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const questions = {
    1: "Hello, tell me what your revenue goal is...I will be your super intelligence to help you reach this goal.",
    2: "Perfect! What's the name of your company, and what products or services would you like to sell?",
    3: "Lastly, what is your Ideal Customer Profile and target market? If not known, I can do my best to find ideal customers."
  };

  const handleStep1Next = () => {
    const goal = parseFloat(formData.revenueGoal);
    if (!formData.revenueGoal || isNaN(goal) || goal < 1000) {
      toast.error("Please enter a revenue goal of at least $1,000");
      return;
    }
    setTyping(true);
    setStep(2);
  };

  const handleStep2Next = () => {
    const response = formData.products;
    if (!response || response.trim().length < 10) {
      toast.error("Please provide details about your company and products");
      return;
    }
    
    // Extract company name from the first sentence or first few words
    const words = response.trim().split(/\s+/);
    const potentialCompanyName = words.slice(0, Math.min(3, words.length)).join(' ');
    setFormData(prev => ({ ...prev, companyName: potentialCompanyName }));
    
    setTyping(true);
    setStep(3);
  };

  const handleSkipICP = () => {
    setFormData(prev => ({ ...prev, icp: "Please help me determine my ideal customer profile based on my products and services." }));
    handleComplete();
  };

  const handleComplete = async () => {
    if (!formData.icp || formData.icp.trim().length < 5) {
      toast.error("Please provide your ICP or choose to skip");
      return;
    }

    setLoading(true);
    setProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + 5;
      });
    }, 150);

    try {
      // Create user profile
      const response = await axios.post(`${API}/users`, {
        companyName: formData.companyName || "My Company",
        products: formData.products,
        revenueGoal: parseFloat(formData.revenueGoal),
        icp: formData.icp
      });

      setProgress(100);
      clearInterval(progressInterval);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Sales machine initialized successfully!");
      navigate(`/dashboard/${response.data.userId}`);
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Error creating user:", error);
      toast.error("Failed to initialize sales machine");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-teal-400/40 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <style>
          {`
            @keyframes twinkle {
              0%, 100% { opacity: 0.2; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.5); }
            }
          `}
        </style>

        <div className="relative z-10 text-center max-w-md">
          <div className="mb-8">
            <Loader2 className="h-16 w-16 text-[#3dd9c5] animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Initializing your sales machine...</h2>
            <p className="text-gray-400 text-sm mb-6">Setting up intelligent agents and analyzing your market</p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-800/50 rounded-full h-2 mb-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#3dd9c5] to-[#2dd4bf] transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-[#3dd9c5] text-sm font-medium">{progress}%</p>

          <div className="mt-8 space-y-2 text-left text-sm text-gray-400">
            <div className={`flex items-center gap-2 transition-opacity ${progress >= 20 ? 'opacity-100' : 'opacity-30'}`}>
              <div className="h-1.5 w-1.5 rounded-full bg-[#3dd9c5]" />
              <span>Analyzing company profile...</span>
            </div>
            <div className={`flex items-center gap-2 transition-opacity ${progress >= 50 ? 'opacity-100' : 'opacity-30'}`}>
              <div className="h-1.5 w-1.5 rounded-full bg-[#3dd9c5]" />
              <span>Researching target market...</span>
            </div>
            <div className={`flex items-center gap-2 transition-opacity ${progress >= 80 ? 'opacity-100' : 'opacity-30'}`}>
              <div className="h-1.5 w-1.5 rounded-full bg-[#3dd9c5]" />
              <span>Initializing AI agents...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] relative overflow-hidden">
      {/* Animated background particles/stars */}
      <div className="absolute inset-0">
        {[...Array(100)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <style>
        {`
          @keyframes twinkle {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 1; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>

      <div className="relative z-10 container mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-16 mt-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_c8cbf3b6-ea21-4936-bc44-fd1ef70ca0ac/artifacts/ui6ezpid_017767_Logo%20FInal%20File-01.png" 
            alt="Intelligent Sales Machines" 
            className="h-48 w-auto mx-auto mb-6 object-contain"
            data-testid="hero-logo"
          />
          <p className="text-xl text-gray-400">
            Create an instant, powerful, sales machine
          </p>
        </div>

        {/* Onboarding Questions */}
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Step 1: Revenue Goal */}
          <div 
            className="transition-all duration-500"
            style={{ opacity: step >= 1 ? 1 : 0 }}
            data-testid="onboarding-step-1"
          >
            {(typing && step === 1) || (!typing && step === 1) ? (
              <div className="mb-6">
                <div className="text-xl text-white mb-8 font-light leading-relaxed">
                  {typing && step === 1 ? (
                    <TypewriterText 
                      text={questions[1]} 
                      onComplete={() => setTyping(false)}
                      delay={30}
                    />
                  ) : (
                    questions[1]
                  )}
                </div>
              </div>
            ) : null}

            {!typing && step === 1 && (
              <div className="flex items-center gap-4 animate-fade-in">
                <div className="flex-1 flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-4 focus-within:border-[#3dd9c5] transition-all">
                  <span className="text-3xl text-gray-400">$</span>
                  <Input
                    type="number"
                    value={formData.revenueGoal}
                    onChange={(e) => setFormData({ ...formData, revenueGoal: e.target.value })}
                    placeholder="1000000"
                    className="bg-transparent border-0 text-white text-2xl h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="revenue-goal-input"
                    autoFocus
                  />
                </div>
                <Button
                  onClick={handleStep1Next}
                  className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white rounded-full h-14 w-14 p-0 shadow-lg shadow-[#3dd9c5]/30"
                  data-testid="step-1-next-btn"
                >
                  <ArrowRight className="h-6 w-6" />
                </Button>
              </div>
            )}
          </div>

          {/* Step 2: Company & Products */}
          {step >= 2 && (
            <div 
              className="transition-all duration-500 animate-fade-in"
              data-testid="onboarding-step-2"
            >
              {(typing && step === 2) || (!typing && step === 2) ? (
                <div className="mb-6">
                  <div className="text-xl text-white mb-8 font-light leading-relaxed">
                    {typing && step === 2 ? (
                      <TypewriterText 
                        text={questions[2]} 
                        onComplete={() => setTyping(false)}
                        delay={30}
                      />
                    ) : (
                      questions[2]
                    )}
                  </div>
                </div>
              ) : null}

              {!typing && step === 2 && (
                <div className="flex items-start gap-4 animate-fade-in">
                  <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 focus-within:border-[#3dd9c5] transition-all">
                    <Textarea
                      value={formData.products}
                      onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                      placeholder="e.g., We are TechCorp, and we provide AI-powered sales automation software for B2B companies..."
                      className="bg-transparent border-0 text-white text-lg min-h-[120px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="company-products-input"
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={handleStep2Next}
                    className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white rounded-full h-14 w-14 p-0 shadow-lg shadow-[#3dd9c5]/30 flex-shrink-0"
                    data-testid="step-2-next-btn"
                  >
                    <ArrowRight className="h-6 w-6" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: ICP */}
          {step >= 3 && (
            <div 
              className="transition-all duration-500 animate-fade-in"
              data-testid="onboarding-step-3"
            >
              {(typing && step === 3) || (!typing && step === 3) ? (
                <div className="mb-6">
                  <div className="text-xl text-white mb-8 font-light leading-relaxed">
                    {typing && step === 3 ? (
                      <TypewriterText 
                        text={questions[3]} 
                        onComplete={() => setTyping(false)}
                        delay={30}
                      />
                    ) : (
                      questions[3]
                    )}
                  </div>
                </div>
              ) : null}

              {!typing && step === 3 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 focus-within:border-[#3dd9c5] transition-all">
                      <Textarea
                        value={formData.icp}
                        onChange={(e) => setFormData({ ...formData, icp: e.target.value })}
                        placeholder="e.g., Mid-market SaaS companies with 50-500 employees, focusing on sales and marketing teams..."
                        className="bg-transparent border-0 text-white text-lg min-h-[120px] resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-testid="icp-input"
                        autoFocus
                      />
                    </div>
                    <Button
                      onClick={handleComplete}
                      className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white rounded-full h-14 w-14 p-0 shadow-lg shadow-[#3dd9c5]/30 flex-shrink-0"
                      data-testid="step-3-complete-btn"
                    >
                      <ArrowRight className="h-6 w-6" />
                    </Button>
                  </div>
                  <div className="text-center">
                    <Button
                      onClick={handleSkipICP}
                      variant="ghost"
                      className="text-[#3dd9c5] hover:text-[#35c4b5] hover:bg-[#3dd9c5]/10"
                      data-testid="skip-icp-btn"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Help me determine ICP
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-6 left-0 right-0 text-center text-xs text-gray-600">
        © Copyright 2024 Intelligent Sales Machines, Inc. – All rights reserved.
      </div>
    </div>
  );
};

export default Onboarding;
